import { splitInternational } from '@/lib/phone';

/** What the global search box understood from the text typed into it. */
export interface SearchIntent {
  /** Text containing "@": look a customer up by email. */
  email?: string;
  /** 7+ digits (punctuation allowed): look a customer up by phone. */
  phone?: { phone: number; countryCode?: number };
  /**
   * Only digits: jump to ride N, customer #N or vehicle N. Not set for a North American phone
   * number (10 digits, or 11 starting with 1): ids are far shorter, and opening such a number
   * as a customer id fails downstream, so it is looked up as a phone only.
   */
  id?: number;
  /**
   * Ten digits starting with 1: not a North American phone (area codes never start with 1) but
   * the shape of a vehicle number (e.g. Cosmo 1100071036), so the vehicle jump is offered first.
   */
  vehicleFirst?: boolean;
  /** Lower-cased text for matching screen names. */
  text: string;
}

/**
 * Reads the global search text: an email, a phone number ("(555) 000-1234", "+1 555 000 1234",
 * "+61 423 345 112") and/or a numeric id. North American numbers (10 digits, or 11 starting with
 * 1) get country code 1; a valid number of another country is split into its dial code and
 * national number; anything else is sent as typed.
 */
export function parseSearch(raw: string): SearchIntent {
  const text = raw.trim().toLowerCase();
  if (!text) return { text };
  if (text.includes('@')) return { text, email: raw.trim() };

  const intent: SearchIntent = { text };
  if (/^\+?[\d\s().-]+$/.test(text)) {
    const digits = text.replace(/\D/g, '');
    // area codes never start with 0 or 1, so ten digits starting with 1 are a vehicle number
    const vehicleNumber = /^\d+$/.test(text) && digits.length === 10 && digits.startsWith('1');
    const northAmerican = !vehicleNumber && (digits.length === 10 || (digits.length === 11 && digits.startsWith('1')));
    if (/^\d+$/.test(text) && digits.length <= 15 && !northAmerican) intent.id = Number(digits);
    if (vehicleNumber) {
      intent.vehicleFirst = true;
      return intent;
    }
    if (digits.length >= 7) {
      if (digits.length === 10) intent.phone = { phone: Number(digits), countryCode: 1 };
      else if (northAmerican) intent.phone = { phone: Number(digits.slice(1)), countryCode: 1 };
      // another country's full number ("61423345112", "+61 423 345 112"): its dial code and
      // national number, as customers are stored; else the digits as typed
      else intent.phone = splitInternational(digits) ?? { phone: Number(digits) };
    }
  }
  return intent;
}
